import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { PasswordChecklist, TextField } from '@/components/forms';
import {
  AUTH_ERROR_CODES,
  isPasswordValid,
  newPasswordSchema,
  PASSWORD_MAX_LENGTH,
  type NewPasswordForm,
} from '@/features/auth';
import { ApiError, UnauthorizedError } from '@/lib/api/errors';
import { tokenStore } from '@/lib/api/token-store';

import { changePassword } from '../api';
import { PROFILE_COPY, PROFILE_ERROR_CODES } from '../constants';

import otpModal from './otp-modal.module.scss';

const COPY = PROFILE_COPY.changePassword;

/** The one message under Confirm (grilling Q10). Precedence is Figma 1683:618436's: the criteria
 *  error wins over a mismatch. The refusal can only hold for a valid, matching pair. */
function messageFor(v: NewPasswordForm, newBlurred: boolean, refused: boolean): string | null {
  if (refused) return COPY.sameAsCurrent; // §8c
  if (newBlurred && !isPasswordValid(v.newPassword)) return COPY.criteria; // §8a
  if (v.confirmPassword && v.confirmPassword !== v.newPassword) return COPY.mismatch; // §9a/§9b
  return null;
}

type NewPasswordStepProps = { onClose: () => void; onExpired: () => void };

/** 117855 §6–§11. Validity is auth's `newPasswordSchema`; the copy is 117855's (plan Decision 1). */
export function NewPasswordStep({ onClose, onExpired }: NewPasswordStepProps) {
  const toast = useToast();
  const mutation = useMutation({ mutationFn: changePassword });
  // Deliberately no zodResolver/handleSubmit: auth's schema carries the auth flows' copy and would put
  // the complexity error under New Password; 117855 wants its own copy, once, under Confirm. So the
  // schema is only the enable gate (`safeParse`) and `messageFor` is the one writer (Decisions 1/2).
  const { control, formState, setError, clearErrors } = useForm<NewPasswordForm>({
    defaultValues: { newPassword: '', confirmPassword: '' },
  });
  const newPassword = useWatch({ control, name: 'newPassword' });
  const confirmPassword = useWatch({ control, name: 'confirmPassword' });
  const values = { newPassword, confirmPassword };
  // §8c: the refusal belongs to the pair the server judged, so editing either field clears it.
  const [refused, setRefused] = useState<NewPasswordForm | null>(null);
  const isRefused =
    refused?.newPassword === newPassword && refused.confirmPassword === confirmPassword;
  const message = messageFor(values, Boolean(formState.touchedFields.newPassword), isRefused);
  const canSubmit = newPasswordSchema.safeParse(values).success && !isRefused;

  // Written through FormField so Confirm turns red and the text sits in its error slot.
  useEffect(() => {
    if (message) setError('confirmPassword', { type: 'manual', message });
    else clearErrors('confirmPassword');
  }, [message, setError, clearErrors]);

  const onSubmit = async () => {
    try {
      const { accessToken } = await mutation.mutateAsync(newPassword);
      tokenStore.setAccessToken(accessToken); // §10b: this device stays signed in
      onClose();
      toast.success({ title: COPY.updated });
    } catch (error) {
      if (error instanceof UnauthorizedError) return; // sessionExpiry owns it (§11c)
      if (error instanceof ApiError && error.code === AUTH_ERROR_CODES.PASSWORD_MUST_BE_DIFFERENT) {
        setRefused(values);
        return;
      }
      if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.otpVerificationRequired) {
        onExpired(); // the 15-minute gate lapsed: back to Send Code, like code expiry
        return;
      }
      toast.error({ title: PROFILE_COPY.genericError, key: 'change-password' }); // §11a
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={COPY.newPasswordTitle}
      subtitle={<span className={otpModal.helper}>{COPY.newPasswordHelper}</span>}
      width={456}
      className={otpModal.modal}
      primaryText={COPY.submit}
      onPrimary={() => void onSubmit()}
      primaryDisabled={!canSubmit}
      loading={mutation.isPending}
    >
      <TextField
        name="newPassword"
        control={control}
        label="New Password"
        type="password"
        autoFocus
        maxLength={PASSWORD_MAX_LENGTH}
        status={message ? 'error' : undefined}
      />
      <TextField
        name="confirmPassword"
        control={control}
        label="Confirm Password"
        type="password"
        maxLength={PASSWORD_MAX_LENGTH}
        disablePaste // 110944: a Confirm pasted from the field above confirms nothing (Decision 7)
      />
      <PasswordChecklist value={newPassword} />
    </Modal>
  );
}
